import { type BadgeColor } from "@jasperlabs/ui";
import {
	InvestingEntityType,
	InvestingEntityAvailableFunds,
	NzPrescribedInvestorRateBasisPoints,
	InvestingEntityFrequencyOfInvestment,
	InvestingEntityReasonForInvesting,
	SourceOfFundsSource,
	InvestingEntityTrustType,
	InvestingEntityCompanyType,
	InvestingEntityPartnershipType,
	InvestingEntityAccountAccess,
	ResidentWithholdingTaxRateBasisPoints,
	DocumentCategory,
	HoldingDistributionSettings,
	BeneficialOwnerContactRelationship,
	TransactionType,
	TransactionTag,
	FatcaClassification,
	DeemedCompliantSubcategory,
	FiReportingType,
	CrsClassification,
	EmailCategory,
} from "@/types";

export const InvestingEntityTypeLabels: Record<InvestingEntityType, string> = {
	[InvestingEntityType.Company]: "Company",
	[InvestingEntityType.Trust]: "Trust",
	[InvestingEntityType.JointIndividual]: "Joint Individual",
	[InvestingEntityType.Individual]: "Individual",
	[InvestingEntityType.Partnership]: "Partnership",
};

export const InvestingEntityTagVariants: Record<
	InvestingEntityType,
	BadgeColor
> = {
	[InvestingEntityType.Trust]: "success",
	[InvestingEntityType.Company]: "purple",
	[InvestingEntityType.Individual]: "info",
	[InvestingEntityType.Partnership]: "yellow",
	[InvestingEntityType.JointIndividual]: "yellow",
};

export const investingEntityAvailableFundsLabels: Record<
	InvestingEntityAvailableFunds,
	string
> = {
	[InvestingEntityAvailableFunds.Under_50K]: "Under $50K",
	[InvestingEntityAvailableFunds.Between_50KAnd_250K]: "Between $50K and $250K",
	[InvestingEntityAvailableFunds.Between_250KAnd_1M]: "Between $250K and $1M",
	[InvestingEntityAvailableFunds.Over_1M]: "Over $1M",
};

export const nzPrescribedInvestorRateBasisPointsLabels: Partial<
	Record<NzPrescribedInvestorRateBasisPoints, string>
> = {
	[NzPrescribedInvestorRateBasisPoints.Unset]: "None",
	[NzPrescribedInvestorRateBasisPoints.Rate_0]: "0%",
	[NzPrescribedInvestorRateBasisPoints.Rate_144]: "1.44%",
	[NzPrescribedInvestorRateBasisPoints.Rate_1050]: "10.5%",
	[NzPrescribedInvestorRateBasisPoints.Rate_1500]: "15%",
	[NzPrescribedInvestorRateBasisPoints.Rate_1750]: "17.5%",
	[NzPrescribedInvestorRateBasisPoints.Rate_2800]: "28%",
	[NzPrescribedInvestorRateBasisPoints.Rate_3000]: "30%",
};

export const investingEntityFrequencyOfInvestmentLabels: Record<
	InvestingEntityFrequencyOfInvestment,
	string
> = {
	[InvestingEntityFrequencyOfInvestment.OneOff]: "One off",
	[InvestingEntityFrequencyOfInvestment.MultipleOffers]: "Ongoing investment",
};

export const sourceOfFundsSourceLabels: Record<SourceOfFundsSource, string> = {
	[SourceOfFundsSource.BusinessIncome]: "Business income",
	[SourceOfFundsSource.Gifts]: "Gifts",
	[SourceOfFundsSource.Inheritances]: "Inheritance",
	[SourceOfFundsSource.LoanDrawdown]: "Loan drawdown",
	[SourceOfFundsSource.RentalIncome]: "Rental income",
	[SourceOfFundsSource.SalaryAndWages]: "Salary and wages",
	[SourceOfFundsSource.SaleOfProperty]: "Sale of asset",
	[SourceOfFundsSource.SaleOfShares]: "Sale of shares",
	[SourceOfFundsSource.InvestmentIncome]: "Investment income",
	[SourceOfFundsSource.Superannuation]: "Superannuation",
	[SourceOfFundsSource.OtherIncome]: "Other income",
};

export const investingEntityReasonForInvestingLabels: Record<
	InvestingEntityReasonForInvesting,
	string
> = {
	[InvestingEntityReasonForInvesting.CapitalGrowth]: "Capital growth",
	[InvestingEntityReasonForInvesting.CapitalPreservation]:
		"Capital preservation",
	[InvestingEntityReasonForInvesting.EstatePlanning]: "Estate planning",
	[InvestingEntityReasonForInvesting.OngoingIncome]: "Ongoing income",
};

export const investingEntityTrustTypeLabels: Record<
	InvestingEntityTrustType,
	string
> = {
	[InvestingEntityTrustType.Estate]: "Estate",
	[InvestingEntityTrustType.FamilyTrust]: "Family Trust",
	[InvestingEntityTrustType.TradingTrust]: "Trading Trust",
	[InvestingEntityTrustType.TestamentaryTrust]: "Testamentary Trust",
	[InvestingEntityTrustType.SuperFundTrust]: "Super Fund Trust",
	[InvestingEntityTrustType.SelfManagedSuperFund]: "Self-Managed Super Fund",
	[InvestingEntityTrustType.CharitableTrust]: "Charitable Trust",
	[InvestingEntityTrustType.RegisteredManagedInvestmentScheme]:
		"Registered Managed Investment Scheme",
	[InvestingEntityTrustType.GovernmentSuperannuationFund]:
		"Government Superannuation Fund",
	[InvestingEntityTrustType.Discretionary]: "Discretionary",
	[InvestingEntityTrustType.NonDiscretionary]: "Non Discretionary",
	[InvestingEntityTrustType.UnitTrust]: "Unit Trust",
	[InvestingEntityTrustType.Other]: "Other",
};

export const investingEntityCompanyTypeLabels: Record<
	InvestingEntityCompanyType,
	string
> = {
	[InvestingEntityCompanyType.LimitedCompany]: "Limited company",
	[InvestingEntityCompanyType.NonProfit]: "Non-profit",
	[InvestingEntityCompanyType.SoleTrader]: "Sole trader",
	[InvestingEntityCompanyType.ProprietaryLimitedCompany]:
		"Proprietary limited company",
	[InvestingEntityCompanyType.PublicCompany]: "Public company",
	[InvestingEntityCompanyType.CompanyLimitedByGuarantee]:
		"Company limited by guarantee",
	[InvestingEntityCompanyType.UnlimitedCompany]: "Unlimited company",
	[InvestingEntityCompanyType.ForeignCompany]: "Foreign company",
	[InvestingEntityCompanyType.IncorporatedAssociation]:
		"Incorporated association",
};

export const investingEntityPartnershipTypeLabels: Record<
	InvestingEntityPartnershipType,
	string
> = {
	[InvestingEntityPartnershipType.LimitedPartnership]: "Limited partnership",
	[InvestingEntityPartnershipType.GeneralPartnership]: "General partnership",
};

export const investingEntityAccountAccessLabels: Record<
	InvestingEntityAccountAccess,
	string
> = {
	[InvestingEntityAccountAccess.Full]: "Full access",
	[InvestingEntityAccountAccess.ReadOnly]: "Read-only",
};

export const residentWithholdingTaxRateBasisPointsLabels: Record<
	ResidentWithholdingTaxRateBasisPoints,
	string
> = {
	[ResidentWithholdingTaxRateBasisPoints.Rate_0]: "0%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_1050]: "10.5%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_1200]: "12%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_1500]: "15%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_1750]: "17.5%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_1900]: "19%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_2000]: "20%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_2200]: "22%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_2800]: "28%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_3000]: "30%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_3250]: "32.5%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_3300]: "33%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_3700]: "37%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_3900]: "39%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_4500]: "45%",
	[ResidentWithholdingTaxRateBasisPoints.Rate_4700]: "47%",
};

export const documentCategoryLabels: Record<DocumentCategory, string> = {
	[DocumentCategory.DistributionStatements]: "Distribution Statements",
	[DocumentCategory.Risk]: "Risk",
	[DocumentCategory.SourceOfFunds]: "Source of funds",
	[DocumentCategory.SourceOfWealth]: "Source of wealth",
	[DocumentCategory.OfferDocuments]: "Offer Documents",
	[DocumentCategory.FinancialReports]: "Financial Reports",
	[DocumentCategory.FundUpdates]: "Fund Updates",
	[DocumentCategory.PerformanceReports]: "Performance Reports",
	[DocumentCategory.SubscriptionDocuments]: "Subscription Documents",
	[DocumentCategory.TaxStatements]: "Tax Statements",
	[DocumentCategory.TaxResidency]: "Tax Residency",
	[DocumentCategory.Authority]: "Authority",
	[DocumentCategory.ChangeOfDetails]: "Change of Details",
	[DocumentCategory.Other]: "Other",
	[DocumentCategory.HoldingStatements]: "Holding Statements",
	[DocumentCategory.HoldingMovements]: "Holding Movements",
	[DocumentCategory.CorporateActions]: "Corporate Actions",
};

/**
 * The document categories a portal filter may offer, sorted by label.
 *
 * Deliberately not every member of the enum. `CORPORATE_ACTIONS` is withheld
 * while `FORMS_MODULE` is off, because the admin Forms module is what files
 * documents under it — until that ships, the option matches nothing an investor
 * can hold.
 *
 * `documentCategoryLabels` above stays exhaustive and keeps the label either
 * way: once the gateway has filed a document under the category, the documents
 * list has to render its name rather than blank. Being readable and being
 * filterable are separate questions — the same split admin-app draws between
 * `investingEntityDocumentCategoryLabels` and
 * `selectableInvestingEntityDocumentCategoryLabels`.
 *
 * A function rather than a constant because the exclusion is runtime state.
 * Call it through `useDocumentCategoryFilterOptions`, which reads the flag,
 * rather than passing a hardcoded value.
 */
export function filterableDocumentCategoryOptions({
	isFormsModuleEnabled,
}: {
	isFormsModuleEnabled: boolean;
}): { label: string; value: DocumentCategory }[] {
	const withheld: DocumentCategory[] = isFormsModuleEnabled
		? []
		: [DocumentCategory.CorporateActions];

	return Object.values(DocumentCategory)
		.filter((category) => !withheld.includes(category))
		.map((value) => ({ label: documentCategoryLabels[value], value }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

export const holdingDistributionSettingsLabels: Record<
	HoldingDistributionSettings,
	string
> = {
	[HoldingDistributionSettings.Enabled]: "Enabled",
	[HoldingDistributionSettings.Disabled]: "Disabled",
	[HoldingDistributionSettings.Withheld]: "Withheld",
};

export const beneficialOwnerContactRelationshipLabels: Record<
	BeneficialOwnerContactRelationship,
	string
> = {
	[BeneficialOwnerContactRelationship.Director]: "Director",
	[BeneficialOwnerContactRelationship.ShareholdingVotingRightsGreaterThan_25Percent]:
		"Shareholding / voting rights > 25%",
	[BeneficialOwnerContactRelationship.GeneralPartner]: "General partner",
	[BeneficialOwnerContactRelationship.Partner]: "Partner",
	[BeneficialOwnerContactRelationship.LimitedPartnerGreaterThan_25Percent]:
		"Limited partner > 25% shareholding / voting",
	[BeneficialOwnerContactRelationship.BeneficiaryGreaterThan_25Percent]:
		"Beneficiary > 25% shareholding / voting",
	[BeneficialOwnerContactRelationship.BearerAndNomineeShareholder]:
		"Bearer & Nominee Shareholder",
	[BeneficialOwnerContactRelationship.Trustee]: "Trustee",
	[BeneficialOwnerContactRelationship.NonDiscretionaryBeneficiary]:
		"Non-Discretionary Beneficiary",
	[BeneficialOwnerContactRelationship.DiscretionaryBeneficiary]:
		"Discretionary Beneficiary",
	[BeneficialOwnerContactRelationship.Settlor]: "Settlor",
	[BeneficialOwnerContactRelationship.AppointorOrProtector]:
		"Appointor or Protector",
	[BeneficialOwnerContactRelationship.NomineeShareholder]:
		"Nominee Shareholder",
	[BeneficialOwnerContactRelationship.OtherAuthorisedParty]:
		"Other authorised party",
	[BeneficialOwnerContactRelationship.ExecutorOrAdministrator]:
		"Executor or Administrator",
	[BeneficialOwnerContactRelationship.PowerOfAttorney]: "Power of Attorney",
	[BeneficialOwnerContactRelationship.MigratedAccount]: "Migrated Account",
};

export const transactionTypeLabels: Record<TransactionType, string> = {
	AUTO_REINVESTMENT: "Reinvestment",
	DISTRIBUTION: "Distribution",
	DISTRIBUTION_PAYMENT: "Distribution Payment",
	FUND_EXIT: "Fund Exit",
	NEW_INVESTMENT: "New Investment",
	SECONDARY_UNIT_PURCHASE: "Secondary Purchase",
	SECONDARY_UNIT_SALE: "Secondary Sale",
	DEPOSIT: "Deposit",
	WITHDRAWAL: "Withdrawal",
	OFF_MARKET: "Off-Market Transfer",
	REDEMPTION: "Redemption",
	TAX_ATTRIBUTION: "Tax Attribution",
};

export const transactionTagLabels: Partial<Record<TransactionTag, string>> = {
	[TransactionTag.AccruedInterest]: "Accrued Interest",
	[TransactionTag.BonusUnits]: "Bonus Units",
	[TransactionTag.Principal]: "Principal",
	[TransactionTag.Stapling]: "Stapling",
};

export const fatcaClassificationLabels: Record<FatcaClassification, string> = {
	[FatcaClassification.ActiveNffe]: "Active NFFE",
	[FatcaClassification.PassiveNffe]: "Passive NFFE",
	[FatcaClassification.ParticipatingFfi]: "Participating FFI",
	[FatcaClassification.DeemedCompliantFfi]: "Deemed-compliant FFI",
	[FatcaClassification.OwnerDocumentedFfi]: "Owner-documented FFI",
	[FatcaClassification.NonParticipatingFfi]: "Non-participating FFI",
	[FatcaClassification.DirectReportingNffe]: "Direct reporting NFFE",
	[FatcaClassification.SponsoredDirectReportingNffe]:
		"Sponsored direct reporting NFFE",
	[FatcaClassification.ExemptBeneficialOwner]: "Exempt beneficial owner",
	[FatcaClassification.UsSpecifiedPerson]: "US specified person",
	[FatcaClassification.Other]: "Other",
};

export const deemedCompliantSubcategoryLabels: Record<
	DeemedCompliantSubcategory,
	string
> = {
	[DeemedCompliantSubcategory.CertifiedDeemedCompliantFfi]:
		"Certified deemed compliant FFI",
	[DeemedCompliantSubcategory.RegisteredDeemedCompliantFfi]:
		"Registered deemed compliant FFI",
	[DeemedCompliantSubcategory.SponsoredInvestmentVehicle]:
		"Sponsored investment vehicle",
	[DeemedCompliantSubcategory.TrusteeDocumentedTrust]:
		"Trustee documented trust",
	[DeemedCompliantSubcategory.Other]: "Other",
};

export const fiReportingTypeLabels: Record<FiReportingType, string> = {
	[FiReportingType.Reporting]: "Reporting FI",
	[FiReportingType.NonReporting]: "Non-reporting FI",
};

export const crsClassificationLabels: Record<CrsClassification, string> = {
	[CrsClassification.ActiveNfe]: "Active NFE",
	[CrsClassification.PassiveNfe]: "Passive NFE",
	[CrsClassification.GovernmentEntity]: "Government entity",
	[CrsClassification.InternationalOrganisation]: "International organisation",
	[CrsClassification.CentralBank]: "Central bank",
	[CrsClassification.PubliclyTradedCorp]: "Publicly traded corporation",
	[CrsClassification.RelatedEntityOfPubliclyTraded]:
		"Related entity of publicly traded corporation",
	[CrsClassification.Other]: "Other (please specify)",
};

export const EmailCategoryLabels: Record<EmailCategory, string> = {
	[EmailCategory.Distributions]: "Distributions",
	[EmailCategory.HoldingAndTaxStatements]: "Holding & Tax Statements",
	[EmailCategory.OrdersAndHoldingMovements]: "Orders & Holding Movements",
	[EmailCategory.UpdatesAndReports]: "Updates & Reports",
};
